import "./btn.css"

import { LoadingSpinner } from "@components/internal"
import {
  BaseComponentProps,
  COMPONENT_KIND_CLASS_MAP,
  COMPONENT_SIZE_CLASS_MAP,
  ComponentKind,
  ComponentSize,
  enhanceElementWithClassName,
} from "@utilities/buttonLinkShared"
import classNames from "classnames"
import React, { ButtonHTMLAttributes, forwardRef } from "react"

export interface BaseButtonProps extends Omit<BaseComponentProps, "kind"> {
  /** The visual style of the button. */
  kind?: ComponentKind
  /** The text content of the button. */
  label: React.ReactNode
  /** Whether the button is in a loading state. When true, the button is disabled and shows a loading spinner. */
  loading?: boolean
  /** Accessible label to use when the button is in loading state. */
  loadingAriaLabel?: string
}

export type BaseButtonElementProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof BaseButtonProps | "children"
> &
  BaseButtonProps

const buildComponentClassName = ({
  className,
  fullWidth,
  kind,
  loading,
  size,
}: {
  className?: string
  fullWidth?: boolean
  kind?: ComponentKind
  loading?: boolean
  size?: ComponentSize
}): string => {
  return classNames(
    kind && "tds-btn",
    size && size !== "md" && COMPONENT_SIZE_CLASS_MAP[size],
    kind && COMPONENT_KIND_CLASS_MAP[kind],
    fullWidth && "tds-btn--full-width",
    loading && "tds-btn--loading",
    className
  )
}

export const BaseButton = forwardRef<HTMLButtonElement, BaseButtonElementProps>(
  (
    {
      "aria-label": ariaLabel,
      className,
      disabled = false,
      fullWidth,
      kind = "secondary",
      label,
      loading = false,
      loadingAriaLabel = "Loading...",
      prefix,
      size,
      suffix,
      ...restProps
    }: BaseButtonElementProps,
    ref
  ) => {
    const combinedClassName = buildComponentClassName({
      className,
      fullWidth,
      kind,
      loading,
      size,
    })

    const prefixElement = enhanceElementWithClassName(prefix, "prefix")
    const suffixElement = enhanceElementWithClassName(suffix, "suffix")

    const isDisabled = disabled || loading

    return (
      <button
        type="button"
        className={combinedClassName}
        ref={ref}
        {...restProps}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
        disabled={isDisabled}
        aria-label={loading ? loadingAriaLabel : ariaLabel}
      >
        {loading && <LoadingSpinner />}
        {prefixElement}
        {loading ? <span>{label}</span> : label}
        {suffixElement}
      </button>
    )
  }
)

BaseButton.displayName = "BaseButton"
